import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Plus } from '@styled-icons/bootstrap/Plus';
import { AlertDialog, ItemCardSmall, ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import truncate from 'lodash/truncate';
import React, { FC, Fragment, useState } from 'react';
import { IPartnerRequest } from 'FeatureModules/Partner/types/partner.types';
import FlexBox from 'Components/FlexBox';
import PageSubTitle from 'Components/PageSubTitle';
import {
  REQUEST_STATUS_CLIENT,
  REQUEST_STATUS_COLOR_MAPPING,
} from 'FeatureModules/Request/utils/requestStatus.utils';

const Status = styled(Typography)<{ status: IPartnerRequest['requestStatus'] }>(
  ({ status, theme }) => ({
    fontSize: 13,
    marginRight: 8,
    textTransform: 'uppercase',

    color: REQUEST_STATUS_COLOR_MAPPING[status],
  })
);

const RequestButton = styled(ThemeButton)(() => ({
  background: 'transparent',
}));

interface IPartnerRequestList {
  totalItems?: number;
  requests: IPartnerRequest[];
  onClickCreate: () => void;
  onClickDelete: (id: IPartnerRequest['id']) => void;
  onAllRequestsClick: () => void;
  onClick: (request: IPartnerRequest) => void;
}

const PartnerRequestList: FC<IPartnerRequestList> = ({
  requests,
  totalItems,
  onClickCreate,
  onClickDelete,
  onAllRequestsClick,
  onClick,
}) => {
  const [showDeleteAlertForId, setShowDeleteAlertForId] = useState<
    IPartnerRequest['id'] | undefined
  >(undefined);

  return (
    <Fragment>
      <Box flex={1} data-testid="requests-list">
        <PageSubTitle
          title="Requests"
          visibleItems={requests.length}
          totalItems={totalItems}
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <RequestButton onClick={onAllRequestsClick} size="small" color="BLUE_ICE">
              See all requests
            </RequestButton>
            <ThemeButton
              onClick={onClickCreate}
              startIcon={<Plus size={16} color="black" />}
              size="small"
              color="WHITE"
              data-testid="button-new-request"
            >
              New request
            </ThemeButton>
          </Box>
        </PageSubTitle>
        <Box data-testid="home-document-container">
          {requests.map(request => (
            <ItemCardSmall
              key={request.id}
              onCardClick={() => onClick(request)}
              // onClickEdit={() => onClickEdit(request)}
              onClickDelete={() => setShowDeleteAlertForId(request.id)}
            >
              <Box display="flex" alignItems="center" gap={2.5}>
                <FlexBox pl={1}>
                  <Icon
                    name={
                      REQUEST_STATUS_CLIENT[request.requestType][request.requestStatus].iconName
                    }
                    size="xx-large"
                  />
                </FlexBox>

                <Box>
                  <Tooltip title={request.requestTitle || ''}>
                    <ThemeTypography variant="ITEM_TITLE">
                      {truncate(request.requestTitle, {
                        length: 45,
                        separator: '',
                      })}
                    </ThemeTypography>
                  </Tooltip>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <Status variant="body2" status={request.requestStatus}>
                      {
                        REQUEST_STATUS_CLIENT[request.requestType][request.requestStatus]
                          .statusTitle
                      }
                    </Status>
                  </Box>
                </Box>
              </Box>
            </ItemCardSmall>
          ))}
        </Box>
      </Box>
      <AlertDialog
        onClose={() => setShowDeleteAlertForId(undefined)}
        title="Delete request"
        open={!!showDeleteAlertForId}
        text="Are you sure you want to delete this request?"
        onSubmit={() => {
          if (showDeleteAlertForId) {
            onClickDelete(showDeleteAlertForId);
            setShowDeleteAlertForId(undefined);
          }
        }}
        onCancel={() => setShowDeleteAlertForId(undefined)}
        submitText="Yes, delete"
      />
    </Fragment>
  );
};

export default PartnerRequestList;
